import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import toast from 'react-hot-toast';
import { Mail, Phone, MessageSquare, Briefcase, Sparkles } from 'lucide-react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useC, useDark } from '@/features/shared/theme/kaziColors';
import { DanaAvatar } from '@/features/shared/components/branding/DanaAvatar';
import { openDana } from '@/lib/dana';
import { conversationsApi } from '@/features/conversations/api/conversationsApi';
import { buildTenantPath, extractTenantSlug } from '@/features/navigation/utils';

/** Copy to clipboard with feedback — a reliable fallback when the device has no
 *  mail/phone handler for mailto:/tel: (common on desktop). */
const copyWithToast = (text: string, msg: string) => {
  navigator.clipboard?.writeText(text).then(() => toast.success(msg)).catch(() => {});
};

interface Employee {
  id: string;
  userId?: string; // auth user id — for opening a direct message
  name: string;
  position: string;
  department: string;
  email: string;
  phone: string;
  status: 'active' | 'on_leave' | 'inactive';
  avatar?: string;
  hireDate: string;
  managerId?: string;
  isAssistant?: boolean;
  isDraft?: boolean;
  incompleteCount?: number;
  contractExpired?: boolean;
}

const AV_COLORS = ['#F28A3A', '#3B82F6', '#22C55E', '#8B5CF6', '#EF4444', '#F6B26B'];

// v5 design-system employee card — inline styles, theme-aware via useC/useDark.
export const EmployeeCard: React.FC<{ employee: Employee }> = ({ employee }) => {
  const { t } = useTranslation(['employees']);
  const C = useC();
  const dark = useDark();
  const navigate = useNavigate();
  const location = useLocation();
  const [hov, setHov] = useState(false);

  // Open (or create) a direct message with this employee — the chat action goes to a
  // real Conversations DM. Someone with no login account can't receive in-app messages,
  // so we point to email instead of dropping the user into Dana.
  const openDM = async () => {
    if (!employee.userId) {
      toast(
        employee.email
          ? t('employees:directory.no_account_email', { name: employee.name, defaultValue: `${employee.name} doesn't have a KaziPeople login yet — email them instead.` })
          : t('employees:directory.no_account', { name: employee.name, defaultValue: `${employee.name} doesn't have a KaziPeople login yet, so they can't receive in-app messages.` }),
        { icon: 'ℹ️' },
      );
      return;
    }
    try {
      const conv = await conversationsApi.createDirect(employee.userId, employee.id);
      const slug = extractTenantSlug(location.pathname);
      navigate(buildTenantPath(`messages/${conv.id}`, slug));
    } catch {
      toast.error(t('employees:directory.message_failed', { defaultValue: 'Could not open the conversation.' }));
    }
  };

  // Draft outranks the mapped status — a Draft maps to 'active' in the UI union, so
  // flag it distinctly ("to activate") to avoid an incomplete hire reading as Active.
  const statusCfg = employee.isDraft
    ? {
        color: C.yellow,
        label: employee.incompleteCount
          ? t('employees:status.draft_count', { defaultValue: 'Draft · {{count}} to activate', count: employee.incompleteCount })
          : t('employees:status.draft', { defaultValue: 'Draft · incomplete' }),
      }
    : {
        active: { color: C.green, label: t('employees:status.active', { defaultValue: 'Active' }) },
        on_leave: { color: C.yellow, label: t('employees:status.on_leave', { defaultValue: 'On Leave' }) },
        inactive: { color: C.red, label: t('employees:status.inactive', { defaultValue: 'Inactive' }) },
      }[employee.status] || { color: C.charcoalSubtle, label: '' };

  const goProfile = () => {
    const base = location.pathname.replace(/\/employees(\/.*)?$/, '/employees');
    navigate(`${base}/${employee.id}`);
  };
  const stop = (e: React.MouseEvent) => e.stopPropagation();
  const initials = (employee.name || '?').split(' ').map((w) => w[0]).join('').slice(0, 2).toUpperCase();
  const avColor = AV_COLORS[(employee.name || '?').charCodeAt(0) % AV_COLORS.length];

  const iconBtn = (primary?: boolean): React.CSSProperties => ({
    flex: 1, padding: '8px 12px', borderRadius: 8,
    border: primary ? 'none' : `1px solid ${C.border}`,
    background: primary ? C.orange : 'transparent',
    display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', transition: 'all 0.2s',
  });

  return (
    <div
      onMouseEnter={() => setHov(true)} onMouseLeave={() => setHov(false)} onClick={goProfile}
      title={statusCfg.label}
      style={{
        background: dark ? C.creamDeep : C.white,
        border: `1px solid ${hov ? C.borderMed : C.border}`,
        borderRadius: 16, padding: '24px 20px', transition: 'all 0.3s',
        transform: hov ? 'translateY(-4px)' : 'none',
        boxShadow: hov ? (dark ? '0 12px 32px rgba(0,0,0,0.3)' : '0 12px 32px rgba(47,52,67,0.1)') : 'none',
        cursor: 'pointer',
      }}
    >
      {/* Avatar + status */}
      <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', marginBottom: 18 }}>
        <div style={{ position: 'relative', marginBottom: 14 }}>
          {employee.isAssistant ? (
            <DanaAvatar size={64} />
          ) : employee.avatar ? (
            <img src={employee.avatar} alt={employee.name} style={{ width: 64, height: 64, borderRadius: '50%', objectFit: 'cover' }} />
          ) : (
            <div style={{ width: 64, height: 64, borderRadius: '50%', background: avColor, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 23, fontWeight: 700, color: '#fff' }}>{initials}</div>
          )}
          {!employee.isAssistant && (
            <div style={{ position: 'absolute', bottom: 2, right: 2, width: 16, height: 16, borderRadius: '50%', background: statusCfg.color, border: `2px solid ${dark ? C.creamDeep : C.white}` }} />
          )}
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 4 }}>
          <h3 style={{ fontSize: 15, fontWeight: 700, color: C.charcoal, textAlign: 'center' }}>{employee.name}</h3>
          {employee.isAssistant && (
            <span style={{ display: 'inline-flex', alignItems: 'center', gap: 3, padding: '2px 7px', borderRadius: 99, background: 'rgba(242,138,58,0.12)', color: C.orange, fontSize: 10, fontWeight: 700 }}>
              <Sparkles size={9} /> AI
            </span>
          )}
        </div>
        <p style={{ fontSize: 12, color: C.charcoalLight, textAlign: 'center' }}>{employee.position}</p>
        {employee.contractExpired && (
          <span style={{ marginTop: 6, padding: '2px 8px', borderRadius: 99, background: 'rgba(239,68,68,0.12)', color: '#dc2626', fontSize: 10, fontWeight: 700 }}
            title="Contract end date has passed while still Active">
            Expired contract
          </span>
        )}
      </div>

      {/* Department chip */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6, marginBottom: 16, padding: '6px 12px', background: C.cream, borderRadius: 8 }}>
        <Briefcase size={12} color={C.charcoalLight} />
        <span style={{ fontSize: 11, color: C.charcoalMid }}>{employee.department}</span>
      </div>

      {/* Actions */}
      <div style={{ display: 'flex', gap: 8 }} onClick={stop}>
        {employee.isAssistant ? (
          <>
            <a href={`mailto:${employee.email}`} style={iconBtn()} title="Email"><Mail size={16} color={C.charcoalMid} /></a>
            <button style={iconBtn(true)} title="Chat with Dana" type="button" onClick={() => openDana()}>
              <MessageSquare size={16} color="#fff" />
            </button>
          </>
        ) : (
          <>
            {employee.email ? (
              <a href={`mailto:${employee.email}`} onClick={() => copyWithToast(employee.email, t('employees:directory.email_copied', { defaultValue: 'Email copied' }))}
                style={iconBtn()} title={`Email ${employee.email}`}><Mail size={16} color={C.charcoalMid} /></a>
            ) : (
              <button style={{ ...iconBtn(), opacity: 0.45, cursor: 'not-allowed' }} title={t('employees:directory.no_email', { defaultValue: 'No email on file' })} type="button" disabled><Mail size={16} color={C.charcoalSubtle} /></button>
            )}
            {employee.phone ? (
              <a href={`tel:${employee.phone}`} onClick={() => copyWithToast(employee.phone, t('employees:directory.phone_copied', { defaultValue: 'Phone number copied' }))}
                style={iconBtn()} title={`Call ${employee.phone}`}><Phone size={16} color={C.charcoalMid} /></a>
            ) : (
              <button style={{ ...iconBtn(), opacity: 0.45, cursor: 'not-allowed' }} title={t('employees:directory.no_phone', { defaultValue: 'No phone number on file' })} type="button" disabled><Phone size={16} color={C.charcoalSubtle} /></button>
            )}
            <button style={iconBtn(true)} title={t('employees:directory.message', { defaultValue: `Message ${employee.name}` })} type="button"
              onClick={openDM}>
              <MessageSquare size={16} color="#fff" />
            </button>
          </>
        )}
      </div>
    </div>
  );
};

export default EmployeeCard;
